import React, { useMemo, useState } from 'react';
import PageHero from '../components/layout/PageHero';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';

const TERMS = [
  { term: 'AUTOSAR', definition: 'AUTomotive Open System ARchitecture — a standardized software architecture for automotive ECUs, jointly developed by manufacturers and suppliers.' },
  { term: 'CAN Bus', definition: 'Controller Area Network — a robust serial bus protocol widely used for communication between ECUs in a vehicle.' },
  { term: 'CRC', definition: 'Cyclic Redundancy Check — a checksum algorithm used to detect accidental changes to raw data, commonly appended to CAN/E2E frames.' },
  { term: 'DTC', definition: 'Diagnostic Trouble Code — a code stored by an ECU when it detects a fault, retrievable over OBD-II or UDS.' },
  { term: 'E2E', definition: 'End-to-End Protection — an AUTOSAR mechanism (CRC, counter, Data ID) that safeguards signal communication between software components.' },
  { term: 'ECU', definition: 'Electronic Control Unit — an embedded system that controls one or more electrical systems in a vehicle.' },
  { term: 'OBD-II', definition: 'On-Board Diagnostics II — a standardized system for vehicle self-diagnostics and reporting, mandatory on most modern vehicles.' },
  { term: 'PID', definition: 'Parameter ID — a code used to request specific diagnostic data (like engine RPM or coolant temperature) via OBD-II.' },
  { term: 'UDS', definition: 'Unified Diagnostic Services (ISO 14229) — a diagnostic communication protocol used for ECU flashing, diagnostics, and testing.' },
  { term: 'Data ID', definition: 'A short identifier embedded in an E2E-protected message to detect misrouted or mismatched signals between sender and receiver.' },
  { term: 'LIN Bus', definition: 'Local Interconnect Network — a low-cost serial protocol used for less critical automotive subsystems, often alongside CAN.' },
  { term: 'FlexRay', definition: 'A deterministic, high-speed automotive bus protocol used in safety-critical applications like steer-by-wire and active suspension.' },
];

const GlossaryPage = () => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TERMS;
    return TERMS.filter(
      (t) => t.term.toLowerCase().includes(q) || t.definition.toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered
      .slice()
      .sort((a, b) => a.term.localeCompare(b.term))
      .forEach((t) => {
        const letter = t.term[0].toUpperCase();
        groups[letter] = groups[letter] || [];
        groups[letter].push(t);
      });
    return groups;
  }, [filtered]);

  return (
    <div className="min-h-screen" data-testid="glossary-page">
      <PageHero
        eyebrow="Resources"
        title="Glossary"
        description="Plain-language definitions for the acronyms and terms used across Articles, Tools, and Community."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="relative max-w-md mb-10">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search terms..."
              className="pl-9"
              data-testid="glossary-search-input"
            />
          </div>

          {Object.keys(grouped).length === 0 ? (
            <p className="text-muted-foreground" data-testid="glossary-no-results">
              No terms match "{query}".
            </p>
          ) : (
            <div className="space-y-10 max-w-3xl">
              {Object.keys(grouped)
                .sort()
                .map((letter) => (
                  <div key={letter}>
                    <h2 className="font-chivo text-xl font-black uppercase tracking-tight text-autoxium-blue mb-4">
                      {letter}
                    </h2>
                    <dl className="space-y-5">
                      {grouped[letter].map((t) => (
                        <div key={t.term} className="border-b border-structure pb-5" data-testid={`glossary-term-${t.term.toLowerCase()}`}>
                          <dt className="font-chivo font-bold uppercase tracking-tight mb-1">{t.term}</dt>
                          <dd className="text-sm text-muted-foreground leading-relaxed">{t.definition}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default GlossaryPage;
